<script lang="ts">
  import { onMount } from 'svelte';
  import { push } from 'svelte-spa-router';
  import Button from '../../Button.svelte';
  import Table from '../../Table.svelte';
  import Pagination from '../../Pagination.svelte';
  import Dropdown from '../../Dropdown.svelte';
  import SearchInput from '../../SearchInput.svelte';
  import UsersService, { type User, type UserFilters } from '../../../lib/api/admin/users.service';
  import { createPreferencesManager } from '../../../lib/utils/preferences';
  import LoadingSkeleton from '../../LoadingSkeleton.svelte';
  import EmptyState from '../../EmptyState.svelte';
  import ErrorState from '../../ErrorState.svelte';
  import toast from 'svelte-french-toast';

  const preferences = createPreferencesManager('user-management');
  const EMPLOYEE_ROLE_UUID = '3d8b53f5-11e2-4f8b-9d3b-bb86dcd1e633';

  let isLoading = true;
  let error = '';
  let users: User[] = [];
  let totalPages = 1;
  let totalItems = 0;

  let searchPhrase = preferences.getFilters().searchPhrase || '';
  let selectedRole = getInitialRoleFilter() || preferences.getFilters().selectedRole || '';

  let currentPage = preferences.getCurrentPage() || 1;
  let itemsPerPage = preferences.getPageSize() || 15;

  const roleOptions: Array<{ uuid: string; name: string }> = [
    { uuid: '', name: 'All User Types' },
    { uuid: EMPLOYEE_ROLE_UUID, name: 'Employee' },
    { uuid: '3d8b53f5-11e2-4f8b-9d3b-bb86dcd1e634', name: 'Charity' },
    { uuid: '3d8b53f5-11e2-4f8b-9d3b-bb86dcd1e635', name: 'Employer' },
    { uuid: '3d8b53f5-11e2-4f8b-9d3b-bb86dcd1e636', name: 'Admin' },
  ];

  const ROLE_DISPLAY_NAMES: Record<string, string> = {
    'User': 'Employee',
    'Charity': 'Charity',
    'Employer': 'Employer',
    'Admin': 'Admin',
  };

  function getRoleDisplayName(roleName: string): string {
    return ROLE_DISPLAY_NAMES[roleName] || roleName;
  }

  function getInitialRoleFilter(): string {
    const hashQuery = window.location.hash.split('?')[1] || '';
    const searchQuery = window.location.search.replace(/^\?/, '');
    const params = new URLSearchParams(hashQuery || searchQuery);
    const role = params.get('role');

    return role === 'employee' ? EMPLOYEE_ROLE_UUID : '';
  }

  function splitName(fullName: string): { firstName: string; lastName: string } {
    if (!fullName) return { firstName: '', lastName: '' };
    const parts = fullName.trim().split(/\s+/);
    const firstName = parts[0] || '';
    const lastName = parts.slice(1).join(' ') || '';
    return { firstName, lastName };
  }

  const tableColumns = [
    {
      key: "full_name",
      label: "First Name",
      width: "auto",
      render: (value: string) => {
        const { firstName } = splitName(value);
        return `<span class="user-name">${firstName}</span>`;
      }
    },
    {
      key: "full_name",
      label: "Last Name",
      width: "auto",
      render: (value: string) => {
        const { lastName } = splitName(value);
        return `<span class="user-name">${lastName || '—'}</span>`;
      }
    },
    {
      key: "email",
      label: "Email",
      width: "auto",
      render: (value: string) => `<span class="email">${value}</span>`
    },
    {
      key: "phone",
      label: "Mobile",
      width: "160px",
      render: (value: string) => {
        if (!value) return '<span class="no-value">—</span>';
        return `<span class="phone">${value}</span>`;
      }
    },
    {
      key: "user_role",
      label: "User Type",
      width: "140px",
      render: (value: any) => {
        if (!value || !value.name) return '<span class="no-value">—</span>';
        const displayName = getRoleDisplayName(value.name);
        const roleClass = `role-badge role-${value.name.toLowerCase()}`;
        return `<span class="${roleClass}">${displayName}</span>`;
      }
    }
  ];

  onMount(() => {
    loadUsers();
  });

  async function loadUsers() {
    try {
      isLoading = true;
      error = '';

      const filters: UserFilters = {
        page: currentPage,
        per_page: itemsPerPage,
        role: selectedRole || 'all'
      };

      if (searchPhrase) filters.phrase = searchPhrase;

      const result = await UsersService.getUsers(filters);

      if (result.success && result.data) {
        users = result.data;
        totalPages = result.paging?.last_page || 1;
        totalItems = result.paging?.total || 0;
      } else {
        error = result.message || 'Failed to load users';
        toast.error(error);
      }
    } catch (err: any) {
      error = err.message || 'Failed to load users';
      toast.error(error);
    } finally {
      isLoading = false;
    }
  }

  function handleSearch() {
    currentPage = 1;
    preferences.update({ currentPage: 1, filters: { searchPhrase, selectedRole } });
    loadUsers();
  }

  function handleRoleChange(value: string) {
    selectedRole = value;
    currentPage = 1;
    preferences.update({ currentPage: 1, filters: { searchPhrase, selectedRole } });
    loadUsers();
  }

  function handlePageChange(event: CustomEvent<{ page: number }>) {
    currentPage = event.detail.page;
    preferences.update({ currentPage });
    loadUsers();
  }

  function handleRowClick(row: User) {
    const roleName = row.user_role?.name;

    switch (roleName) {
      case 'User':
        push(`/admin/employee-profile/${row.uuid}`);
        break;
      case 'Employer': {
        const employerUuid = row.organization?.data?.uuid;
        if (employerUuid) {
          push(`/employer-profile/${employerUuid}`);
        }
        break;
      }
      case 'Charity': {
        const charityUuid = row.organization?.data?.uuid;
        if (charityUuid) {
          push(`/charity-profile/${charityUuid}`);
        }
        break;
      }
      default:
        break;
    }
  }

  function handleResetFilters() {
    searchPhrase = '';
    selectedRole = '';
    currentPage = 1;
    preferences.reset();
    loadUsers();
  }

  $: if (searchPhrase !== undefined || selectedRole !== undefined) {
    preferences.updateFilters({ searchPhrase, selectedRole });
  }

  $: if (itemsPerPage !== undefined) {
    preferences.update({ pageSize: itemsPerPage });
  }

  $: if (currentPage !== undefined) {
    preferences.update({ currentPage });
  }
</script>

<div class="manage-list-page">
  <div class="page-header">
    <p class="subtitle">Manage and monitor all users across the platform</p>
  </div>

  <div class="filters-section">
    <div class="filters-left">
      <div class="filter-item">
        <SearchInput
          bind:value={searchPhrase}
          placeholder="Search users"
          on:search={handleSearch}
          width="250px"
        />
      </div>

      <div class="filter-item">
        <Dropdown
          options={roleOptions}
          selectedValue={selectedRole}
          placeholder="All User Types"
          onSelectionChange={handleRoleChange}
        />
      </div>
    </div>

    <div class="filters-right">
      <Button variant="primary" on:click={handleSearch}>
        Search
      </Button>
      {#if searchPhrase || selectedRole}
        <Button variant="secondary" on:click={handleResetFilters}>
          Reset Filters
        </Button>
      {/if}
    </div>
  </div>

  <div class="table-section">
    {#if error}
      <ErrorState
        message="Failed to load users"
        description={error}
        onRetry={loadUsers}
      />
    {:else if isLoading}
      <div class="loading-wrap">
        <LoadingSkeleton lines={10} height="2.5rem" />
      </div>
    {:else if !users || users.length === 0}
      <EmptyState
        message="No users found"
        description={searchPhrase || selectedRole ? "Try adjusting your filters to see more results." : "No users registered yet."}
        actionLabel={searchPhrase || selectedRole ? "Reset Filters" : null}
        onAction={searchPhrase || selectedRole ? handleResetFilters : null}
      />
    {:else}
      <Table
        columns={tableColumns}
        data={users}
        onRowClick={handleRowClick}
      />

      {#if totalPages > 1}
        <div class="pagination-container">
          <Pagination
            {currentPage}
            {totalPages}
            on:pageChange={handlePageChange}
          />
        </div>
      {/if}
    {/if}
  </div>
</div>

<style>
  .page-header {
    margin-bottom: 1.5rem;
  }

  .subtitle {
    margin: 0;
    font-size: 0.875rem;
    color: #6b7280;
    line-height: 1.5;
  }

  .filters-section {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    flex-wrap: wrap;
    gap: 1rem;
    margin-bottom: 1rem;
    width: 100%;
  }

  .filters-left {
    display: flex;
    gap: 1rem;
    flex: 1;
    flex-wrap: wrap;
    align-items: flex-start;
    width: 100%;
  }

  .filter-item {
    flex: 1 1 auto;
    min-width: 140px;
    max-width: 300px;
  }

  .filter-item :global(.search-input-wrapper),
  .filter-item :global(.dropdown-wrapper) {
    width: 100%;
  }

  .filters-right {
    display: flex;
    gap: 0.75rem;
    flex-shrink: 0;
  }

  .table-section {
    background: white;
    border-radius: 12px;
    border: 1px solid #e5e7eb;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
    overflow: hidden;
  }

  .loading-wrap {
    padding: 2rem;
    min-height: 320px;
  }

  .pagination-container {
    padding: 1rem 1.5rem;
    border-top: 1px solid #f3f4f6;
  }

  :global(.user-name) {
    font-weight: 500;
    color: #111827;
  }

  :global(.email) {
    color: #6b7280;
  }

  :global(.phone) {
    color: #374151;
  }

  :global(.no-value) {
    color: #9ca3af;
  }

  :global(.role-badge) {
    display: inline-flex;
    align-items: center;
    padding: 0.25rem 0.75rem;
    border-radius: 9999px;
    font-size: 0.75rem;
    font-weight: 500;
    white-space: nowrap;
  }

  :global(.role-badge.role-user) {
    background-color: #dbeafe;
    color: #1e40af;
  }

  :global(.role-badge.role-employer) {
    background-color: #d1fae5;
    color: #065f46;
  }

  :global(.role-badge.role-charity) {
    background-color: #fce7f3;
    color: #9d174d;
  }

  :global(.role-badge.role-admin) {
    background-color: #f3f4f6;
    color: #4b5563;
  }

  @media (max-width: 768px) {
    .filters-section {
      flex-direction: column;
      align-items: stretch;
    }

    .filters-left {
      flex-direction: column;
      max-width: none;
    }

    .filters-right {
      justify-content: flex-end;
    }
  }
</style>
